/**
 * leadActionPopupResolver
 * ────────────────────────
 * Single source of truth for "which logical popup flow does this click open?".
 *
 * The problem this solves: today the popup that opens is decided by *where* the
 * user clicked (lead table vs overview vs task board vs WhatsApp …). The same
 * lead status + same intent can therefore open different UI and load different
 * data. This resolver makes the decision a pure function of (status, action) so
 * every fire point routes the same way.
 *
 * Usage at a fire point:
 *
 *   const { popup } = resolveLeadActionPopup({
 *     practiceId, leadId, leadTreatmentId, taskId,
 *     status,          // raw lead_treatments[*].status (or 'ARCHIVED')
 *     action,          // optional explicit intent, e.g. ACTION.RECORD_ATTENDED
 *     source,          // free-form origin tag for debugging/forensics
 *   });
 *   switch (popup) { ... }
 *
 * This module owns ONLY the routing decision. The data rendered inside the
 * popup is normalised separately (Track 1 — checklistViewModel) and the
 * activity-state safety rules (Phase 0) live in the Redux selectors.
 */

// ─── Canonical lead/treatment statuses ───────────────────────────────────────
// Mirrors the lifecycle documented in CLAUDE.md. ARCHIVED is not a
// lead_treatment status value (it is derived from lead.archived_at) but the
// resolver accepts it as an input status — the caller is responsible for
// passing 'ARCHIVED' when the lead is archived.
export const STATUS = Object.freeze({
    NEW: 'NEW',
    CONSULTATION_BOOKED: 'CONSULTATION_BOOKED',
    CONSULTATION_ATTENDED: 'CONSULTATION_ATTENDED',
    FAILED_TO_ATTEND_CONSULTATION: 'FAILED_TO_ATTEND_CONSULTATION',
    TREATMENT_STARTED: 'TREATMENT_STARTED',
    CLOSED: 'CLOSED',
    ARCHIVED: 'ARCHIVED',
});

// Legacy / shorthand aliases seen across the codebase, mapped to canonical.
const STATUS_ALIASES = Object.freeze({
    FAILED_TO_ATTEND: STATUS.FAILED_TO_ATTEND_CONSULTATION,
    FTA: STATUS.FAILED_TO_ATTEND_CONSULTATION,
    ATTENDED: STATUS.CONSULTATION_ATTENDED,
    BOOKED: STATUS.CONSULTATION_BOOKED,
});

// ─── Action intents ──────────────────────────────────────────────────────────
// A subset of these "hard route" (override the status default); the rest are
// recognised but non-overriding (they open the current-status flow).
export const ACTION = Object.freeze({
    OPEN_TASK: 'OPEN_TASK',
    // Product decision (raj, 2026-06-19): LOG_PHONE_CALL is intentionally
    // NON-overriding — a logged call always opens the flow for the lead's
    // current status (NEW -> follow-up V1/V2, BOOKED -> booking, ATTENDED ->
    // attended). Do NOT add it to ACTION_TO_POPUP.
    LOG_PHONE_CALL: 'LOG_PHONE_CALL',
    BOOK_APPOINTMENT: 'BOOK_APPOINTMENT',
    EDIT_APPOINTMENT: 'EDIT_APPOINTMENT',
    RESCHEDULE_APPOINTMENT: 'RESCHEDULE_APPOINTMENT',
    RECORD_ATTENDED: 'RECORD_ATTENDED',
    RECORD_FTA: 'RECORD_FTA',
    START_TREATMENT: 'START_TREATMENT',
    COMPLETE_CHECKLIST_ITEM: 'COMPLETE_CHECKLIST_ITEM',
});

// ─── Logical popups (flows) ──────────────────────────────────────────────────
// These are *logical* flows, not component names. The fire-point migration maps
// each to a concrete popup component (e.g. FOLLOW_UP -> ContactLeadPopupRouter).
export const POPUP = Object.freeze({
    FOLLOW_UP: 'FOLLOW_UP',
    CONSULTATION_BOOKED: 'CONSULTATION_BOOKED',
    ATTENDED: 'ATTENDED',
    FTA: 'FTA',
    TREATMENT_STARTED: 'TREATMENT_STARTED',
    READ_ONLY: 'READ_ONLY',
    TASK_DETAIL: 'TASK_DETAIL',
    BOOK_APPOINTMENT: 'BOOK_APPOINTMENT',
    EDIT_APPOINTMENT: 'EDIT_APPOINTMENT',
    RESCHEDULE_APPOINTMENT: 'RESCHEDULE_APPOINTMENT',
});

// Actions that hard-route to a specific popup, overriding the status default.
const ACTION_TO_POPUP = Object.freeze({
    [ACTION.OPEN_TASK]: POPUP.TASK_DETAIL,
    [ACTION.BOOK_APPOINTMENT]: POPUP.BOOK_APPOINTMENT,
    [ACTION.EDIT_APPOINTMENT]: POPUP.EDIT_APPOINTMENT,
    [ACTION.RESCHEDULE_APPOINTMENT]: POPUP.RESCHEDULE_APPOINTMENT,
    [ACTION.RECORD_ATTENDED]: POPUP.ATTENDED,
    [ACTION.RECORD_FTA]: POPUP.FTA,
    [ACTION.START_TREATMENT]: POPUP.TREATMENT_STARTED,
});

// The status default mapping.
const STATUS_TO_POPUP = Object.freeze({
    [STATUS.CONSULTATION_BOOKED]: POPUP.CONSULTATION_BOOKED,
    [STATUS.CONSULTATION_ATTENDED]: POPUP.ATTENDED,
    [STATUS.FAILED_TO_ATTEND_CONSULTATION]: POPUP.FTA,
    [STATUS.TREATMENT_STARTED]: POPUP.TREATMENT_STARTED,
    [STATUS.CLOSED]: POPUP.READ_ONLY,
    [STATUS.ARCHIVED]: POPUP.READ_ONLY,
    [STATUS.NEW]: POPUP.FOLLOW_UP,
});

/**
 * Normalise a raw status string to a canonical STATUS value.
 * Empty / nullish → NEW. Unknown values pass through (upper-cased) so callers
 * keep the information and the resolver's default still routes them sensibly.
 */
export function normalizeStatus(status) {
    if (!status || !String(status).trim()) return STATUS.NEW;
    const key = String(status).trim().toUpperCase();
    return STATUS_ALIASES[key] || key;
}

/**
 * Normalise a raw action string to a recognised ACTION value, or null.
 * Returning null for unknown/empty actions means they cannot override status.
 */
export function normalizeAction(action) {
    if (!action || !String(action).trim()) return null;
    const key = String(action).trim().toUpperCase();
    return ACTION[key] || null;
}

/**
 * Resolve the logical popup for a click context.
 *
 * NOTE for migrators: an empty/unknown status resolves to FOLLOW_UP (fail-safe
 * default). If you are replacing a `switch` whose `default` branch was a no-op,
 * guard the FOLLOW_UP case on the raw status so an unrecognised status stays a
 * no-op instead of opening the contact popup (see OverView.jsx btnHandler).
 *
 * @param {object} ctx - { practiceId, leadId, leadTreatmentId, taskId, status, action, source }
 * @returns {{ popup: string, ctx: object }} the chosen logical popup + the original ctx
 */
export function resolveLeadActionPopup(ctx) {
    const safeCtx = ctx || {};
    const status = normalizeStatus(safeCtx.status);
    const action = normalizeAction(safeCtx.action);

    const overridden = action && ACTION_TO_POPUP[action];
    const popup = overridden || STATUS_TO_POPUP[status] || POPUP.FOLLOW_UP;

    return { popup, ctx: safeCtx };
}
